import { groq } from "next-sanity"

import { getAssignedCustomersFilter, groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getMultipleCustomersByNumbers } from "../../shared/utils/getCustomer"
import { visitReportProjection } from "../sanity/visitReport.projection"
import { visitReportOccasionProjection } from "../sanity/visitReportsOccasion.projection"

export const queryVisitReports = {
  async visitReports(parent, args, context, info) {
    const { year, customerNumber, cdn = false, lang = AppSettings.defaultLocale() } = args
    const { user } = context

    const client = context.sanityClient

    const config = AppSettings.sanityConfigByLang(lang)
    client.config({ ...config, useCdn: cdn })

    let visitReports = null

    await checkPermissions({
      user,
      domainObject: DomainObjects.VISIT_REPORTS,
      operation: Operations.READ,
    })

    if (customerNumber) {
      await validateAccessRightsToCustomer({
        user: user,
        customerNumber: customerNumber,
      })
    }

    if (year === "months" || isNaN(Number(year))) {
      visitReports = await querySanityVisitReports(args, context, client)
    }

    if (!isNaN(Number(year)) || !visitReports) {
      return await queryDbVisitReports(args, context, client)
    }

    return visitReports
  },
}

/**
 * Queries visitReports from last 3 months from sanity
 *
 * @param {object} args
 * @param {object} context
 * @param {sanityClient} client
 *
 * @returns {Promise>visitReports|null>}
 *
 */
const querySanityVisitReports = async (args, context, client) => {
  const { customerNumber, offset, limit, sort, sortWithinLimits, occasionId, searchTerm } = args

  let customerNumberFilter = ""

  const { user } = context

  if (customerNumber) {
    customerNumberFilter = `&& customerNumber == "${customerNumber}"`
  } else {
    customerNumberFilter = await getAssignedCustomersFilter({ user })
  }

  const { pipesAndLimits, draftsFilter } = groqQueryOptions({
    sort,
    offset,
    limit,
    sortWithinLimits,
    includeDrafts: true,
  })

  let searchTermFilter = searchTerm ? `&& (text match "*${searchTerm}*") ` : ""

  const occasionFilter = occasionId ? `&& "${occasionId}" in occasions[]._ref` : ""

  const visitReports = await client.fetch(
    groq`{
        "total": count(*[_type=="visitReport"  ${customerNumberFilter}${occasionFilter}${draftsFilter}]),
        "items": *[_type=="visitReport" ${customerNumberFilter}${occasionFilter}${searchTermFilter}${draftsFilter}]${pipesAndLimits} {
          ${visitReportProjection()}
        }
      }`,
    { customerNumber: customerNumber },
  )

  if (visitReports.items && visitReports.items.length !== 0) {
    const { items, total } = visitReports

    const customerNumbers = items.map((item) => item.customerNumber).filter((n) => n)
    const visitReportCustomers = await getMultipleCustomersByNumbers(customerNumbers)

    return {
      total,
      items: items.map((visitReport) => {
        let attendanceAt = visitReport?.attendanceAt

        if (Array.isArray(attendanceAt)) {
          attendanceAt = new Date(attendanceAt).toISOString().split("T")[0]
        }

        const customer = visitReportCustomers.find((customer) => customer.customerNumber === visitReport.customerNumber)

        return {
          ...visitReport,
          ...(visitReport?.attendanceAt && {
            attendanceAt,
          }),
          companyName: customer?.companyName,
          readonly: false,
        }
      }),
    }
  }

  return null
}

/**
 * Queries visitReports from the db and returns visit reprots or null
 *
 * @param {object} context
 * @param {object} args
 * @param {sanityClient} client
 *
 * @returns {Promise<visitReports>}
 */
const queryDbVisitReports = async (args, context, client) => {
  const { customerNumber, limit, sort, occasionId, year, page, searchTerm } = args

  const { user } = context

  const admNumber = user?.loggedInAdm.admNumber

  const yearFilter = year ? { field: "year", operator: "eq", value: year.toString() } : false
  const occasionFilter = await getOccasionFilter(client, occasionId)
  let searchTermFilter = searchTerm && { field: "text", operator: "like", value: searchTerm }

  let filterValues = [occasionFilter, yearFilter, searchTermFilter].filter((filter) => filter)

  const hasFilters = occasionFilter || yearFilter

  const method = getDbQueryMethod(hasFilters, customerNumber)

  const graphiQLQueryObject = [
    {
      method,
      variables: {
        ...(customerNumber ? { customerNumber } : { admNumber }),
        ...(hasFilters && { filters: filterValues }),
        page,
        size: limit,
        ...(sort && { sort: EinzaGraphiQLService.formatSortParam(sort) }),
      },
      fields: ["total: totalElements"],
      expandedFields: {
        content: [
          "id: sysUuid",
          "customerNumber",
          "contactName",
          "attendanceStart",
          "attendanceEnd",
          "meetingName",
          "occasions",
          "mailRecipients",
          "mailSubject",
          "text",
          "creationAt",
          "attendanceAt",
        ],
      },
    },
  ]

  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
  const visitReportsResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.visitReports

  if (!visitReportsResponse?.content) {
    console.error("queryDbVisitReports()", { customerNumber, limit, sort, occasionId, year, page })
    return null
  }

  const customerNumbers = visitReportsResponse?.content?.map((item) => item.customerNumber).filter((n) => n)

  const visitReportCustomers = await getMultipleCustomersByNumbers(customerNumbers)

  return {
    total: visitReportsResponse?.total || 0,
    items:
      visitReportsResponse?.content?.map((visitReport) => {
        const customer = visitReportCustomers.find((customer) => customer.customerNumber === visitReport.customerNumber)

        return {
          ...visitReport,
          companyName: customer?.companyName,
          readonly: true,
        }
      }) || [],
  }
}

/**
 * Retrieves method name used for graphql queries
 *
 * @param {false|object} hasFilters
 * @param {string} customerNumber
 *
 * @returns {string}
 */
const getDbQueryMethod = (hasFilters, customerNumber) => {
  const methodName = "visitReports"

  if (customerNumber) {
    return hasFilters
      ? `${methodName}: findVisitReportsByCustomerAndFilter`
      : `${methodName}: findVisitReportsByCustomer`
  }

  return hasFilters ? `${methodName}: findVisitReportsByAdmAndFilter` : `${methodName}: findVisitReportsByAdm`
}

/**
 * Retrieves filter used for occasions
 *
 * @param {sanityClient} client
 * @param {string} occasionId
 * @returns {Promise<false|object>}
 */
const getOccasionFilter = async (client, occasionId) => {
  if (occasionId) {
    const occasion = await client.fetch(
      groq`*[_type=="occasion" && _id==$id][0] {
        ${visitReportOccasionProjection()}
      }`,
      { id: occasionId },
    )

    return occasion?.label ? { field: "occasion", operator: "eq", value: occasion?.name } : false
  }

  return false
}
